import React, { useState, useEffect } from 'react';
import { Filter, X, Clock, MapPin, Phone, Mail, AlertCircle, Package, Calendar, DollarSign, Save, ArrowUpDown, PenTool as Tool, Truck, Check, Trash2 } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import * as Toast from '@radix-ui/react-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { format } from 'date-fns';
import { Booking, BookingStatus } from '../../types';
import { getOrders, updateOrder, deleteOrder } from '../../utils/orders';
import { supabase } from '../../utils/supabase';
import { DeleteConfirmDialog } from '../../components/admin/DeleteConfirmDialog';

const timeSlots = [
  '9:00 AM - 11:00 AM',
  '11:00 AM - 1:00 PM',
  '1:00 PM - 3:00 PM',
  '3:00 PM - 5:00 PM'
];

export function Orders() {
  const [orders, setOrders] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Booking | null>(null);
  const [newStatus, setNewStatus] = useState<BookingStatus | null>(null);
  const [installDate, setInstallDate] = useState('');
  const [installTime, setInstallTime] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [filters, setFilters] = useState({
    status: 'All',
    dateRange: 'all',
    searchQuery: ''
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [orderToDelete, setOrderToDelete] = useState<Booking | null>(null);

  useEffect(() => {
    fetchOrders(true);

    const subscription = supabase
      .channel('orders_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        () => {
          fetchOrders();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchOrders = async (showLoader = false) => {
    try {
      if (showLoader) setIsLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder || !newStatus) return;

    try {
      setIsSaving(true);
      setSaveError(null);

      const updatedOrder = {
        ...selectedOrder,
        status: newStatus,
        delivery_schedule: newStatus === 'Confirmed Install Date' && installDate && installTime
          ? { date: installDate, time: installTime }
          : selectedOrder.delivery_schedule,
        status_history: [
          ...(selectedOrder.status_history || []),
          {
            status: newStatus,
            timestamp: new Date().toISOString(),
            note: statusNote.trim() || `Status updated to ${newStatus}`
          }
        ]
      };

      const { success, error } = await updateOrder(updatedOrder);

      if (!success) {
        throw new Error(error || 'Failed to update order status');
      }

      setSelectedOrder(null);
      setNewStatus(null);
      setStatusNote('');
      setInstallDate('');
      setInstallTime('');
      setToastMessage('Order status updated successfully');
      setShowToast(true);
      await fetchOrders();
    } catch (err) {
      console.error('Error updating order status:', err);
      const msg = err instanceof Error ? err.message : 'Failed to update order status';
      setSaveError(msg);
      setToastMessage(msg);
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (order: Booking) => {
    try {
      const { success, error } = await deleteOrder(order.id);

      if (!success) {
        throw new Error(error || 'Failed to delete order');
      }

      setOrderToDelete(null);
      setToastMessage('Order deleted successfully');
      setShowToast(true);
      await fetchOrders();
    } catch (err) {
      console.error('Error deleting order:', err);
      setOrderToDelete(null);
      setToastMessage('Failed to delete order');
      setShowToast(true);
    }
  };

  const filteredOrders = React.useMemo(() => {
    return orders.filter((order: Booking) => {
      if (filters.status !== 'All' && order.status !== filters.status) {
        return false;
      }

      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        return (
          order.customer_name.toLowerCase().includes(searchLower) ||
          order.order_number.toLowerCase().includes(searchLower) ||
          order.customer_email?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    }).sort((a: Booking, b: Booking) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [orders, filters, sortOrder]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Orders</h1>
        
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search orders..."
            value={filters.searchQuery}
            onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
            className="px-4 py-2 border rounded-lg"
          />

          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="flex items-center px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr 
                  key={order.id}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {order.order_number}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {order.customer_name}
                    </div>
                    {order.customer_email && (
                      <div className="text-sm text-gray-500">
                        {order.customer_email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {order.product.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      ${order.product.price.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      order.status === 'Done' ? 'bg-green-100 text-green-800' :
                      order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      order.status === 'Return/Refund' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(order.created_at), 'MMM d, yyyy h:mm a')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setNewStatus(order.status);
                          setInstallDate(order.delivery_schedule?.date || '');
                          setInstallTime(order.delivery_schedule?.time || '');
                          setSaveError(null);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setOrderToDelete(order)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <Dialog.Root open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50" />
            <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <Dialog.Title className="text-xl font-semibold">
                  Order Details - {selectedOrder.order_number}
                </Dialog.Title>
                <Dialog.Close className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </Dialog.Close>
              </div>

              <div className="space-y-6">
                {/* Status Update Section */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-4">Update Status</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-1">
                        Status
                      </label>
                      <select
                        value={newStatus || selectedOrder.status}
                        onChange={(e) => setNewStatus(e.target.value as BookingStatus)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Confirmed Install Date">Confirmed Install Date</option>
                        <option value="Done">Done</option>
                        <option value="Return/Refund">Return/Refund</option>
                        <option value="Voided">Voided</option>
                      </select>
                    </div>

                    {newStatus === 'Confirmed Install Date' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-1">
                            Installation Date
                          </label>
                          <input
                            type="date"
                            value={installDate}
                            onChange={(e) => setInstallDate(e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-blue-800 mb-1">
                            Installation Time
                          </label>
                          <select
                            value={installTime}
                            onChange={(e) => setInstallTime(e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            <option value="">Select time slot...</option>
                            {timeSlots.map((slot) => (
                              <option key={slot} value={slot}>{slot}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-blue-800 mb-1">
                        Add Note
                      </label>
                      <textarea
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        rows={3}
                        placeholder="Add a note about this status update..."
                      />
                    </div>

                    {saveError && (
                      <div className="flex items-start p-3 bg-red-50 rounded-md text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        {saveError}
                      </div>
                    )}

                    <button
                      onClick={handleStatusUpdate}
                      disabled={isSaving || !newStatus}
                      className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {selectedOrder.customer_email || 'No email provided'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {selectedOrder.customer_phone}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Installation Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Installation Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                      <div className="ml-2">
                        <p className="text-sm font-medium text-gray-900">
                          Installation Address
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedOrder.delivery_address.street}<br />
                          {selectedOrder.delivery_address.city}, {selectedOrder.delivery_address.state} {selectedOrder.delivery_address.zipCode}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Tool className="w-4 h-4 text-gray-400 mt-1" />
                      <div className="ml-2">
                        <p className="text-sm font-medium text-gray-900">
                          Installation Location
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedOrder.installation_location}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status History */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">
                    Status History
                  </h3>
                  <div className="space-y-4">
                    {selectedOrder.status_history.map((update, index) => (
                      <div key={index} className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Clock className="w-4 h-4 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            {update.status}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(update.timestamp), 'MMM d, yyyy h:mm a')}
                          </p>
                          {update.note && (
                            <p className="mt-1 text-sm text-gray-600">
                              {update.note}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Delete Confirmation Dialog */}
      {orderToDelete && (
        <DeleteConfirmDialog
          isOpen={!!orderToDelete}
          onClose={() => setOrderToDelete(null)}
          onConfirm={() => handleDelete(orderToDelete)}
          orderNumber={orderToDelete.order_number}
        />
      )}

      {/* Toast Notifications */}
      <Toast.Provider>
        <Toast.Root
          open={showToast}
          onOpenChange={setShowToast}
          className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg"
        >
          <Toast.Description>{toastMessage}</Toast.Description>
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}